import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BODY_TYPES, PHASES } from "@/lib/lunelle-data";
import { Reveal, MaskLines, Eyebrow } from "./Reveal";
import { useAccount, useProfile } from "@/lib/account";
import { useFaith } from "@/lib/useFaith";
import { mealFor, whyFor } from "@/lib/faith-customs";
import { FaithTable } from "@/components/account/FaithTable";
import { RecipeCard } from "./RecipeCard";
import { GroceryList } from "./GroceryList";
import { KitchenViewSwitch, SavedRecipes } from "./SavedRecipes";

const IMG = { ecto: "/img/bowl2.webp", meso: "/img/bowl1.webp", endo: "/img/bowl3.webp" };

export const Kitchen = () => {
  const [type, setType] = useState("meso");
  const bt = BODY_TYPES.find((b) => b.id === type);
  const { member, profile } = useProfile();
  useEffect(() => {
    if (member && profile?.body_type) setType(profile.body_type);
  }, [member, profile?.body_type]);
  const { openAuth, kitchenView } = useAccount();
  const [myFaith] = useFaith();
  const faith = member ? myFaith : null;
  return (
    <section id="kitchen" className="relative bg-[#F4E6DC] py-28 sm:py-40" data-testid="kitchen-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>02 — The body-type kitchen</Eyebrow></Reveal>
        <h2 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[1] tracking-tight text-[#2C221E] sm:text-6xl lg:text-7xl">
          <MaskLines inView delay={0} lines={["What to eat, for", <><em className="italic text-[#9E4429]">your</em> body, today.</>]} />
        </h2>

        <div className="mt-16 grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <Reveal className="relative">
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] [clip-path:inset(0_round_2rem)]">
              {BODY_TYPES.map((b) => (
                <motion.img key={b.id} src={IMG[b.id]} alt={`${b.name} meal`} animate={{ opacity: type === b.id ? 1 : 0, scale: type === b.id ? 1 : 1.08 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 h-full w-full object-cover" />
              ))}
            </div>
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-[#FDFBF7]/85 p-5 backdrop-blur-xl" data-testid="kitchen-bodytype-card">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9E4429]">{bt.tag}</span>
              <p className="mt-1 font-serif text-2xl text-[#2C221E]">{bt.note}</p>
            </div>
          </Reveal>

          <div id="saved-recipes">
            <KitchenViewSwitch />
            {kitchenView === "saved" ? <SavedRecipes faith={faith} /> : (<>
            <div className="grid grid-cols-3 gap-2 rounded-full border border-[#C86D51]/20 bg-[#FDFBF7]/70 p-1.5">
              {BODY_TYPES.map((b) => (
                <button key={b.id} onClick={() => setType(b.id)} data-testid={`kitchen-tab-${b.id}`}
                  className={`relative rounded-full py-3 text-sm font-medium transition-colors ${type === b.id ? "text-[#FBF5EE]" : "text-[#5C4A42]"}`}>
                  {type === b.id && <motion.span layoutId="body-pill" className="absolute inset-0 rounded-full bg-[#9E4429]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
                  <span className="relative">{b.name}</span>
                </button>
              ))}
            </div>
            <ol className="mt-10 divide-y divide-[#C86D51]/20 border-y border-[#C86D51]/20">
              {PHASES.map((p, i) => (
                <motion.li key={p.id + type} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07, duration: 0.5 }}
                  className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 py-6 sm:grid-cols-[140px_1fr]" data-testid={`kitchen-meal-${p.id}`}>
                  <span className="font-serif text-xl italic" style={{ color: p.color }}>{p.name}</span>
                  <span className="col-span-2 text-[#3D302B] sm:col-span-1">{mealFor(p.id, type, faith)}
                    <span className="mt-1.5 block text-sm leading-relaxed text-[#6B5A52]" data-testid={`kitchen-why-${p.id}`}><strong className="font-semibold text-[#9E4429]">Why it helps:</strong> {whyFor(p.id, type)}</span>
                    <RecipeCard phase={p.id} body={type} faith={faith} phaseName={p.name} />
                  </span>
                </motion.li>
              ))}
            </ol>
            </>)}
            {member && <FaithTable faith={faith} />}
            <GroceryList faith={faith} />
            <p className="mt-6 text-xs leading-relaxed text-[#6B5A52]">Guidance is educational and not medical advice. Lunelle adapts to allergies, preferences and dietary laws such as halal, kosher and vegetarian.
              {!member && <> <button type="button" onClick={() => openAuth("register")} data-testid="kitchen-faith-signup-link" className="font-semibold text-[#9E4429] underline underline-offset-2">Create an account</button> to match every plate to your faith.</>}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
